window.global ||= window; // https://github.com/vitejs/vite/issues/2778#issuecomment-810086159

import React, { Suspense, useCallback } from "react";
import { createRoot } from "react-dom/client";

import { Authenticator } from "@aws-amplify/ui-react";
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import { LicenseInfo } from "@mui/x-data-grid-pro";
import { StackletUIThemeProvider, theme, RouterProvider } from "@stacklet/ui";
import { ErrorBoundary } from "react-error-boundary";
import { RelayEnvironmentProvider } from "react-relay/hooks";
import { BrowserRouter, resolvePath, useHref, useNavigate } from "react-router";

import { Login } from "app/components/authentication/Login";
import { CircularFallback, ErrorFallback } from "app/components/fallbacks";
import { AnalyticsProvider, ConfigProvider } from "app/contexts";
import { ApiExplorerProvider } from "app/contexts/ApiExplorerContext";
import { ThemeProvider as ColorSchemeProvider } from "app/contexts/ThemeContext";

import { App } from "./App";
import { bootstrapStacklet } from "./bootstrap";
import { initSentry, handleComponentError } from "./initSentry";
import { RelayEnvironment } from "./RelayEnvironment";
import { reportWebVitals } from "./reportWebVitals";

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_X_LICENSE ?? "");

// Supports weights 100-900
import "@fontsource-variable/dm-sans";
// Weight 400 only
import "@fontsource/dm-mono";

const container = document.getElementById("root");
// eslint-disable-next-line typescript/no-non-null-assertion
const root = createRoot(container!);

// react-router's useHref treats external URLs as relative paths, breaking
// shouldClientNavigate's same-origin check and routing external links through
// navigate(). This custom hook calls useHref only for internal paths.
// It is invoked by react-aria Link components during their render, satisfying hook rules.
function useRouterSafeHref(href: string): string {
  const resolved = useHref(href);
  return href.startsWith("http://") || href.startsWith("https://")
    ? href
    : resolved;
}

// Wrapper to integrate react-aria-components RouterProvider with react-router.
// RAC's RouterProvider calls navigate() with the raw href prop (not the
// useHref-resolved value), so relative paths would resolve from the root.
// Wrapping navigate to resolve against window.location.pathname at click time fixes this.
function ReactAriaRouterProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();

  const handleNavigate = useCallback(
    (to: string) => {
      if (to.startsWith("http://") || to.startsWith("https://")) {
        // Fallback: external URL that bypassed shouldClientNavigate (shouldn't normally occur).
        window.location.href = to;
        return;
      }
      navigate(
        to.startsWith("/") ? to : resolvePath(to, window.location.pathname),
      );
    },
    [navigate],
  );

  return (
    <RouterProvider navigate={handleNavigate} useHref={useRouterSafeHref}>
      {children}
    </RouterProvider>
  );
}

async function run() {
  await bootstrapStacklet();
  await initSentry();

  root.render(
    <React.StrictMode>
      <ConfigProvider>
        <ApiExplorerProvider>
          <ColorSchemeProvider>
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={theme}>
                <StackletUIThemeProvider>
                  <BrowserRouter>
                    <ReactAriaRouterProvider>
                      <ErrorBoundary
                        FallbackComponent={({ resetErrorBoundary }) => (
                          <ErrorFallback
                            message="Unable to load the application. You may not have the necessary permissions or there may be a system error. If the problem persists, please contact Stacklet support."
                            resetErrorBoundary={resetErrorBoundary}
                            tryAgain
                          />
                        )}
                        onError={handleComponentError}
                      >
                        <Authenticator.Provider>
                          <Login>
                            <RelayEnvironmentProvider
                              environment={RelayEnvironment}
                            >
                              <Suspense fallback={<CircularFallback />}>
                                <AnalyticsProvider>
                                  <App />
                                </AnalyticsProvider>
                              </Suspense>
                            </RelayEnvironmentProvider>
                          </Login>
                        </Authenticator.Provider>
                      </ErrorBoundary>
                    </ReactAriaRouterProvider>
                  </BrowserRouter>
                </StackletUIThemeProvider>
              </ThemeProvider>
            </StyledEngineProvider>
          </ColorSchemeProvider>
        </ApiExplorerProvider>
      </ConfigProvider>
    </React.StrictMode>,
  );
}

run();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
